import React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-8">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full">
        <div className="flex items-center gap-4 mb-6 text-red-500">
          <AlertCircle className="w-8 h-8" />
          <h2 className="text-xl font-semibold">Something went wrong</h2>
        </div>
        
        <div className="bg-red-50 rounded-lg p-4 mb-6">
          <p className="text-red-700 font-mono text-sm break-words">{error.message}</p>
          {error.stack && (
            <details className="mt-2">
              <summary className="text-sm text-red-600 cursor-pointer hover:text-red-700">
                Show error details
              </summary>
              <pre className="mt-2 text-xs text-red-600 overflow-x-auto">
                {error.stack}
              </pre>
            </details>
          )}
        </div>

        <div className="flex gap-4">
          <button
            onClick={resetErrorBoundary}
            className="flex-1 bg-gold text-white py-3 rounded-lg font-medium hover:bg-gold-dark transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Try again
          </button>

          <button
            onClick={() => window.location.href = '/'}
            className="flex-1 bg-white text-gold border-2 border-gold py-3 rounded-lg font-medium hover:bg-gold/5 transition-colors flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" />
            Go to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export const AppErrorBoundary: React.FC<{ 
  children: React.ReactNode;
  onReset?: () => void;
}> = ({ children, onReset }) => {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // Custom reset logic if provided
        if (onReset) {
          onReset();
        } else {
          // Default reset behavior
          window.location.reload();
        }
      }}
      onError={(error) => {
        // Log error to your error tracking service
        console.error('Application Error:', error);
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
};